import React, { useState } from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Toolbar, Box, Typography, AppBar, IconButton, useMediaQuery, Button } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import BookIcon from '@mui/icons-material/Book';
import ScienceIcon from '@mui/icons-material/Science';
import BugReportIcon from '@mui/icons-material/BugReport';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { DownhillSkiing as SkiIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

const drawerWidth = 240;

const MENU = [
  { key: 'my-teams', label: 'My Teams', icon: <GroupIcon /> },
  { key: 'search-teams', label: 'Search Teams', icon: <SearchIcon /> },
  { key: 'waxing-diary', label: 'Waxing Diary', icon: <BookIcon /> },
  { key: 'wax-tests', label: 'Wax Tests', icon: <ScienceIcon /> },
  { key: 'ski-fleet', label: 'Ski Fleet', icon: <SkiIcon /> },
  { key: 'report-bug', label: 'Report Bug', icon: <BugReportIcon /> },
  { key: 'profile', label: 'Profile', icon: <AccountCircleIcon /> },
];

export default function CoachLayout({ selected, onSelect, children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const drawer = (
    <Box sx={{
      width: drawerWidth,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderRight: `1px solid ${theme.palette.divider}`,
    }}>
      <Box>
        <Toolbar />
        <Box sx={{ overflow: 'auto', mt: 2 }}>
          <Typography variant="h6" align="center" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            Coach Menu
          </Typography>
          <List>
            {MENU.map(item => (
              <ListItem button key={item.key} selected={selected === item.key} onClick={() => { onSelect(item.key); setMobileOpen(false); }}
                sx={{
                  color: selected === item.key ? theme.palette.primary.main : theme.palette.text.primary,
                  background: selected === item.key ? 'rgba(99,102,241,0.08)' : 'transparent',
                  borderRadius: 2,
                  mb: 0.5,
                  '&:hover': {
                    background: 'rgba(99,102,241,0.12)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
      <Box sx={{ p: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          startIcon={<ExitToAppIcon />}
          onClick={handleLogout}
          sx={{
            borderColor: 'rgba(255,255,255,0.15)',
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              background: 'rgba(99,102,241,0.08)',
            },
          }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: theme.palette.background.default }}>
      {isMobile ? (
        <>
          <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1, background: theme.palette.background.paper, color: theme.palette.text.primary }}>
            <Toolbar>
              <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 2 }}>
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div">
                Coach Menu
              </Typography>
            </Toolbar>
          </AppBar>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                background: theme.palette.background.paper,
                color: theme.palette.text.primary,
                borderRight: `1px solid ${theme.palette.divider}`,
              },
            }}
          >
            {drawer}
          </Drawer>
          <Box component="main" sx={{ flexGrow: 1, minHeight: '100vh', background: theme.palette.background.default, p: { xs: 2, sm: 3 }, width: '100%', color: theme.palette.text.primary }}>
            <Toolbar />
            {React.cloneElement(children, { selected })}
          </Box>
        </>
      ) : (
        <>
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              display: { xs: 'none', sm: 'block' },
              [`& .MuiDrawer-paper`]: {
                width: drawerWidth,
                boxSizing: 'border-box',
                background: theme.palette.background.paper,
                color: theme.palette.text.primary,
                borderRight: `1px solid ${theme.palette.divider}`,
              },
            }}
            open
          >
            {drawer}
          </Drawer>
          <Box component="main" sx={{ flexGrow: 1, minHeight: '100vh', background: theme.palette.background.default, p: { xs: 2, sm: 4 }, width: '100%', color: theme.palette.text.primary }}>
            <Toolbar />
            {React.cloneElement(children, { selected })}
          </Box>
        </>
      )}
    </Box>
  );
} 